import {
  type ConversationDockPreview,
  type ConversationDockSide,
  type ConversationLayoutNode,
  dockLayoutPane,
  layoutHasPane,
  removeLayoutPane,
} from '@/lib/conversations/conversation-layout';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { activatePanelTab, type PanelTabs } from '@/lib/side-panel/panel-tabs';

/**
 * The tabs shown side by side in the panel, in the split's order. A split names tabs, not views:
 * two pages of the browser, two terminals, show side by side as well.
 */
export function panelSplitTabs(split: ConversationLayoutNode | null): string[] {
  if (split === null) return [];
  if (split.kind === 'pane') return [split.id];
  return [...panelSplitTabs(split.first), ...panelSplitTabs(split.second)];
}

/** A split left with one tab is no split. */
function normalized(split: ConversationLayoutNode | null): ConversationLayoutNode | null {
  return split === null || split.kind === 'pane' ? null : split;
}

/**
 * Splits the panel: shows the tab `tabId` beside the tab `targetId` (the one shown, or one of the
 * split shown), on `side`, and makes it the active tab. A tab already in the split moves there;
 * a split the target is not part of gives way to the new one.
 */
export function splitPanelTab(
  store: DesktopShellStore,
  tabId: string,
  targetId: string,
  side: ConversationDockSide,
): void {
  const state = store.getState();
  const open = (id: string) => state.panelTabs.tabs.some((tab) => tab.id === id);
  if (tabId === targetId || !open(tabId) || !open(targetId)) return;
  const current = state.panelSplit === null ? null : removeLayoutPane(state.panelSplit, tabId);
  const base: ConversationLayoutNode =
    current !== null && layoutHasPane(current, targetId) ? current : { kind: 'pane', id: targetId };
  state.setPanelSplit(dockLayoutPane(base, targetId, tabId, side));
  state.setPanelTabs((tabs) => activatePanelTab(tabs, tabId));
}

/** Takes a tab out of the split: it stays a tab of the panel. */
export function unsplitPanelTab(store: DesktopShellStore, tabId: string): void {
  store
    .getState()
    .setPanelSplit((split) => (split === null ? null : normalized(removeLayoutPane(split, tabId))));
}

/** The split without the tabs that are no longer open. */
export function panelSplitWithTabs(
  split: ConversationLayoutNode | null,
  tabs: PanelTabs,
): ConversationLayoutNode | null {
  let next = split;
  for (const id of panelSplitTabs(split))
    if (!tabs.tabs.some((tab) => tab.id === id) && next !== null) next = removeLayoutPane(next, id);
  return next === split ? split : normalized(next);
}

/**
 * Where a tab dragged over the panel would go: beside the tab under the pointer (the one shown,
 * or a tab of the split shown), on the side of it the pointer is nearest.
 */
export function panelSplitPreviewAt(x: number, y: number): ConversationDockPreview | null {
  const pane = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-panel-split-target]');
  const targetId = pane?.dataset['panelSplitTarget'];
  if (pane === null || pane === undefined || targetId === undefined) return null;
  const bounds = pane.getBoundingClientRect();
  const horizontal = (x - bounds.left) / bounds.width - 0.5;
  const vertical = (y - bounds.top) / bounds.height - 0.5;
  const side: ConversationDockSide =
    Math.abs(horizontal) > Math.abs(vertical)
      ? horizontal < 0
        ? 'left'
        : 'right'
      : vertical < 0
        ? 'top'
        : 'bottom';
  return {
    targetId,
    side,
    left: side === 'right' ? bounds.left + bounds.width / 2 : bounds.left,
    top: side === 'bottom' ? bounds.top + bounds.height / 2 : bounds.top,
    width: side === 'left' || side === 'right' ? bounds.width / 2 : bounds.width,
    height: side === 'top' || side === 'bottom' ? bounds.height / 2 : bounds.height,
  };
}
